// ✅ ALTERED ONLY FOR COLOR SCHEME (Orange + Blue, professional)
// - Replaced strong pastel blocks with soft tints + left accent stripe
// - Unified page background + surfaces
// - Announcements/Events accordions inherit card tint
// - Calendar background uses same page bg
// - Added small helpers: cardSx + surfacePaperSx
// - Replaced getRandomColor() with palette rotation (still simple)

import React, { useState, useEffect, useMemo } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Autocomplete,
} from "@mui/material";
import { RRule } from "rrule";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { API_URL, API_UPLOAD_URL } from "./config";

const localizer = momentLocalizer(moment);

// ✅ Brand + UI palette (Orange + Blue)
const COLORS = {
  primaryBlue: "#1976D2",
  accentOrange: "#F07828",
  pageBg: "#F5F7FA",
  surface: "#FFFFFF",
  border: "#E6EAF0",
  text: "#1F2937",
  textMuted: "#6B7280",
};

const TINTS = {
  dangerBg: "#FDECEA",
  danger: "#C62828",
  infoBg: "#EAF2FF",
  info: "#1E5AA8",
  successBg: "#EAF7EF",
  success: "#1B7F3A",
  warnBg: "#FFF4E5",
  warn: "#B45309",
};

const cardSx = (bg, stripe) => ({
  backgroundColor: bg,
  border: `1px solid ${COLORS.border}`,
  borderLeft: `6px solid ${stripe}`,
  borderRadius: 2,
  boxShadow: "0 2px 8px rgba(16,24,40,0.06)",
});

const surfacePaperSx = {
  backgroundColor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 2,
};

const SUBJECT_PALETTE = [
  COLORS.primaryBlue,
  COLORS.accentOrange,
  TINTS.success,
  "#7B1FA2",
  "#00838F",
];

const HomePage = () => {
  const [logincampusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [userRoles, setUserRoles] = useState([]);
  const [feeSummary, setFeeSummary] = useState({});
  const [userCampus, setCampus] = useState({});
  const [userFullName, setFullName] = useState({});
  const [allstudentsfeeSummary, setAllStudentsFeeSummary] = useState({});
  const [allstudentsfeeDetails, setAllStudentsFeeDetails] = useState([]);
  const [feeDueExpanded, setFeeDueExpanded] = useState(false);

  const [attendance, setAttendance] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("day");
  const [calevents, setcalEvents] = useState([]);
  const [subjectColors, setSubjectColors] = useState({});
  //const { RRule } = require("rrule");
  const [minTime, setMinTime] = useState(new Date());
  const [maxTime, setMaxTime] = useState(new Date());
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [allCalEvents, setAllCalEvents] = useState([]);
  const [userTypeFilter, setUserTypeFilter] = useState(""); // "" = All
  const [selectedImage, setSelectedImage] = useState(null);
  const logoWrapRef = React.useRef(null);
  const token = localStorage.getItem("auth");

  const BASE_DAY = new Date(1970, 0, 1);

  function timeOnlyDate(h, m = 0) {
    return new Date(1970, 0, 1, h, m, 0, 0);
  }


  const isStudent = useMemo(() => {
    const roles = Array.isArray(userRoles) ? userRoles : [];
    return roles.includes("Student");
  }, [userRoles]);

  const isAdmin = useMemo(() => {
    const roles = Array.isArray(userRoles) ? userRoles : [];
    return roles.includes("Admin");
  }, [userRoles]);

  // logo upload states
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalLogoFilename, setOriginalLogoFilename] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoObjectUrl, setLogoObjectUrl] = useState(null);
  const [logoError, setLogoError] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef(null);

  const [showLogoActions, setShowLogoActions] = useState(false);


  // --- Registration Modal State ---
  const [openRegModal, setOpenRegModal] = useState(false);
  const [selectedEventForReg, setSelectedEventForReg] = useState(null);
  const [openUnregModal, setOpenUnregModal] = useState(false);
  const [selectedEventForUnreg, setSelectedEventForUnreg] = useState(null);
  const [regForm, setRegForm] = useState({
    student_name: "",
    roll_number: "",
    branch: "",
    year: "",
    email: "",
    gender: "",
    phone_number: "",
    campus: "",
  });

  const handleOpenRegModal = (event) => {
    setSelectedEventForReg(event);
    setRegForm({
      student_name: userFullName?.FullName || "",
      roll_number: username === "Admin" || isAdmin ? "" : (username || ""),
      branch: "",
      year: "",
      email: "",
      gender: "",
      phone_number: "",
      campus: "",
    });
    setOpenRegModal(true);
  };

  const handleCloseRegModal = () => {
    setOpenRegModal(false);
    setSelectedEventForReg(null);
  };

  const handleOpenUnregModal = (event) => {
    setSelectedEventForUnreg(event);
    setOpenUnregModal(true);
  };

  const handleCloseUnregModal = () => {
    setOpenUnregModal(false);
    setSelectedEventForUnreg(null);
  };

  const handleRegFormChange = (e) => {
    const { name, value } = e.target;
    setRegForm(prev => ({ ...prev, [name]: value }));
  };

  const handleRegSubmit = async () => {
    try {
      const payload = {
        event_id: selectedEventForReg.EventID || selectedEventForReg.id,
        event_title: selectedEventForReg.Title,
        ...regForm,
      };
      await axios.post(`${API_URL}/api/student/events/register`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError("Successfully registered for the event!");
      fetchEvents();
      handleCloseRegModal();
    } catch (err) {
      console.error("Registration error:", err);
      const errorMessage = err.response?.data?.error || "Failed to register for the event. Please try again.";
      setError(errorMessage);
    }
  };

  const handleUnregSubmit = async () => {
    try {
      const payload = {
        event_id: selectedEventForUnreg.EventID || selectedEventForUnreg.id,
        roll_number: username === "Admin" || isAdmin ? "" : (username || ""),
      };
      await axios.post(`${API_URL}/api/student/events/unregister`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setError("Successfully unregistered from the event!");
      fetchEvents();
      handleCloseUnregModal();
    } catch (err) {
      console.error("Unregister error:", err);
      const errorMessage = err.response?.data?.error || "Failed to unregister from the event. Please try again.";
      setError(errorMessage);
    }
  };

  const handleCollapseToggle = () => setIsCollapsed((p) => !p);
  const handleViewChange = (newView) => setView(newView);

  // Fetch CampusCode & Username from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode);
      setUsername(decodedToken.username);
      setUserRoles(decodedToken.roles);
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Failed to decode authentication token.");
    }
  }, [token]);

  // Click outside to close logo action panel
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!showLogoActions) return;
      const wrap = logoWrapRef.current;
      if (wrap && !wrap.contains(e.target)) setShowLogoActions(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [showLogoActions]);

  const toggleLogoActions = () => {
    if (!isAdmin) return;
    setShowLogoActions((p) => !p);
  };

  // Fetch Data on Load
  useEffect(() => {
    if (!logincampusCode || !username) return;

    fetchFeeSummary();

    if (!isStudent) {
      fetchAllStudentsFeeSummary();
      fetchAllStudentsFeeDetails();
    } else {
      setFeeDueExpanded(false);
      setAllStudentsFeeDetails([]);
    }

    fetchCampus();
    fetchUserFullName();
    fetchAttendance();
    fetchAnnouncements();
    fetchcalEvents();
    fetchAssignments();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logincampusCode, username, isStudent]);

  // Normalize fee details rows
  const feeDetailRows = useMemo(() => {
    const d = allstudentsfeeDetails;
    const rows = Array.isArray(d)
      ? d
      : Array.isArray(d?.rows)
        ? d.rows
        : Array.isArray(d?.details)
          ? d.details
          : [];

    return rows.map((r) => ({
      FeeType: r.FeeType ?? r.feetype ?? r.Fee ?? r.feeType ?? "",
      StudentCount: Number(r.StudentCount ?? r.count ?? r.Student_Count ?? 0),
      TotalDue: Number(r.TotalDue ?? r.totalDue ?? r.total_due ?? 0),
    }));
  }, [allstudentsfeeDetails]);

  const renderFeeDetailsTable = () => {
    if (!feeDetailRows.length) {
      return (
        <Typography variant="body2" sx={{ mt: 1, color: COLORS.textMuted }}>
          No fee details available.
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ mt: 1, ...surfacePaperSx }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: COLORS.pageBg }}>
            <TableRow>
              <TableCell>
                <b>FeeType</b>
              </TableCell>
              <TableCell align="right">
                <b>Count</b>
              </TableCell>
              <TableCell align="right">
                <b>FeeDue</b>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {feeDetailRows.map((r, idx) => (
              <TableRow key={`${r.FeeType}-${idx}`}>
                <TableCell>{r.FeeType || "—"}</TableCell>
                <TableCell align="right">{r.StudentCount}</TableCell>
                <TableCell align="right">
                  {Number(r.TotalDue || 0).toLocaleString("en-IN")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const fetchUserFullName = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/FullName`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = response.data;
      const fullnameObj = Array.isArray(d) ? d[0] || {} : d || {};
      setFullName(fullnameObj);
    } catch (err) {
      setError("Failed to fetch Full Name.");
      setFullName({});
    }
  };

  const fetchCampus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/CampusName`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = response.data;
      const campusObj = Array.isArray(d) ? d[0] || {} : d || {};
      setCampus(campusObj);

      const logo = campusObj?.CampusLogo;

      if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
      setLogoObjectUrl(null);
      setLogoFile(null);
      setLogoError("");

      if (logo) {
        setOriginalLogoFilename(logo);
        const isFullUrl = /^https?:\/\//i.test(String(logo));
        const url = isFullUrl ? logo : `${API_UPLOAD_URL}/uploads/${logo}`;
        setLogoPreview(url);
      } else {
        setOriginalLogoFilename(null);
        setLogoPreview(null);
      }
    } catch (err) {
      setError("Failed to fetch Campus Name.");
      setCampus({});
      setLogoPreview(null);
    }
  };

  const pickLogo = () => {
    if (!isAdmin) return;
    logoInputRef.current?.click();
  };

  const onLogoSelect = (e) => {
    setLogoError("");
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) return setLogoError("Only JPG / PNG / WEBP allowed.");
    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) return setLogoError("Logo must be ≤ 2 MB.");

    const url = URL.createObjectURL(file);
    if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);

    setLogoObjectUrl(url);
    setLogoFile(file);
    setLogoPreview(url);
    setShowLogoActions(true);
  };

  const removeLogoSelection = () => {
    setLogoError("");
    if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
    setLogoObjectUrl(null);
    setLogoFile(null);

    if (originalLogoFilename) {
      const isFullUrl = /^https?:\/\//i.test(String(originalLogoFilename));
      const url = isFullUrl
        ? originalLogoFilename
        : `${API_UPLOAD_URL}/uploads/${originalLogoFilename}`;
      setLogoPreview(url);
    } else setLogoPreview(null);

    setShowLogoActions(false);
  };

  const uploadCampusLogo = async () => {
    if (!logoFile) return;
    setLogoError("");

    try {
      setUploadingLogo(true);
      const fd = new FormData();
      fd.append("campusLogo", logoFile);

      const uploadRes = await axios.post(`${API_URL}/api/uploadCampusLogo`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const filename = uploadRes.data?.filename;
      if (!filename) throw new Error("No filename returned");

      await fetchCampus();

      if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
      setLogoObjectUrl(null);
      setLogoFile(null);
      setShowLogoActions(false);
    } catch (e) {
      console.error(e);
      setLogoError(e?.response?.data?.message || "Failed to upload campus logo.");
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => {
    return () => {
      if (logoObjectUrl) URL.revokeObjectURL(logoObjectUrl);
    };
  }, [logoObjectUrl]);

  // Fetchers
  const fetchFeeSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/fee-summary`, {
        params: { StudentID: username },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeeSummary(response.data || {});
    } catch (err) {
      setError("Failed to fetch Fee Summary.");
    }
  };

  const fetchAllStudentsFeeSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/allstudents/fee-summary`, {
        params: { StudentID: username },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllStudentsFeeSummary(response.data || {});
    } catch (err) {
      setError("Failed to fetch Fee Summary.");
    }
  };

  const fetchAllStudentsFeeDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/allstudents/fee-details`, {
        params: { StudentID: username },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllStudentsFeeDetails(response.data ?? []);
    } catch (err) {
      console.error("fee-details error:", err?.response?.data || err);
      setError("Failed to fetch Fee Details.");
      setAllStudentsFeeDetails([]);
    }
  };

  const fetchAttendance = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/attendance`, {
        params: { StudentID: username },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAttendance(response.data || {});
    } catch (err) {
      setError("Failed to fetch Attendance.");
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnnouncements(response.data || []);
    } catch (err) {
      setError("Failed to fetch Announcements.");
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/student/assignments`, {
        params: { StudentID: username },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignments(response.data || []);
    } catch (err) {
      console.warn("Failed to fetch Assignments (API might not exist yet).");
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/student/events`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    
      const normalizedEvents = (
        response.data || []
      ).map((event) => ({
        ...event,
        IsUserRegistered:
          Number(event.IsUserRegistered) === 1,
        RegisteredCount:
          Number(event.RegisteredCount || 0),
        SeatsCount:
          event.SeatsCount === null ||
          event.SeatsCount === undefined
            ? null
            : Number(event.SeatsCount),
      }));
    
      setEvents(normalizedEvents);
    } catch (err) {
      console.error(
        "Failed to fetch Events:",
        err
      );
    
      setError("Failed to fetch Events.");
    }
  };

  // ✅ Instead of always returning same blue, rotate palette deterministically
  const getSubjectColor = (subjectCode) => {
    const s = String(subjectCode || "X");
    let sum = 0;
    for (let i = 0; i < s.length; i++) sum += s.charCodeAt(i);
    return SUBJECT_PALETTE[sum % SUBJECT_PALETTE.length];
  };

  const fetchcalEvents = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/student/schedule`, {
        params: { StudentID: username },
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawEvents = Array.isArray(data) ? data : [];
      const windowStart = new Date();
      const windowEnd = new Date();
      windowEnd.setMonth(windowEnd.getMonth() + 6);
      const MAX_OCCURRENCES = 500;

      const allEvents = rawEvents.flatMap((evt) => {
        const startMs = new Date(evt.StartTime).getTime();
        const durMin = Number(evt.DurationMinutes);
        const durMs =
          Number.isFinite(durMin) && durMin > 0 ? durMin * 60_000 : NaN;

        if (!Number.isFinite(startMs) || !Number.isFinite(durMs)) return [];

        const base = {
          ...evt,
          title: `${evt.SubjectNameCode} (Teacher: ${evt.TeacherName}, Section: ${evt.Section}, Room: ${evt.RoomNumber})`,
        };

        if (evt.RecurrenceRule) {
          let rule;
          try {
            rule = RRule.fromString(evt.RecurrenceRule);
          } catch {
            return [];
          }

          return rule
            .between(windowStart, windowEnd, true)
            .slice(0, MAX_OCCURRENCES)
            .map((d) => ({
              ...base,
              start: d,
              end: new Date(d.getTime() + durMs),
            }));
        }

        const start = new Date(startMs);
        const end = new Date(startMs + durMs);
        return [{ ...base, start, end }];
      });

      setAllCalEvents(allEvents);

      const filtered =
        userTypeFilter && userTypeFilter !== ""
          ? allEvents.filter((e) => e.UserType === userTypeFilter)
          : allEvents;

      setcalEvents(filtered);
      /*
      if (filtered.length) {
        const starts = filtered.map((e) => e.start.getTime());
        const ends = filtered.map((e) => e.end.getTime());
        setMinTime(new Date(Math.min(...starts)));
        setMaxTime(new Date(Math.max(...ends)));
      }
      */
      if (filtered.length) {
        const startDates = filtered.map((e) => e.start).filter((d) => d instanceof Date && !isNaN(d));
        const endDates = filtered.map((e) => e.end).filter((d) => d instanceof Date && !isNaN(d));

        // fallback if something is weird
        if (!startDates.length || !endDates.length) {
          setMinTime(timeOnlyDate(8, 0));
          setMaxTime(timeOnlyDate(18, 0));
          return;
        }

        // compute earliest start time-of-day and latest end time-of-day
        let minH = 23, minM = 59;
        let maxH = 0, maxM = 0;

        for (const d of startDates) {
          const h = d.getHours();
          const m = d.getMinutes();
          if (h < minH || (h === minH && m < minM)) {
            minH = h; minM = m;
          }
        }

        for (const d of endDates) {
          const h = d.getHours();
          const m = d.getMinutes();
          if (h > maxH || (h === maxH && m > maxM)) {
            maxH = h; maxM = m;
          }
        }

        // padding (so UI doesn’t clip)
        minH = Math.max(0, minH - 1);
        maxH = Math.min(23, maxH + 1);

        const minT = timeOnlyDate(minH, 0);
        const maxT = timeOnlyDate(maxH, 59);

        // ensure min < max
        if (maxT.getTime() <= minT.getTime()) {
          setMinTime(timeOnlyDate(8, 0));
          setMaxTime(timeOnlyDate(18, 0));
        } else {
          setMinTime(minT);
          setMaxTime(maxT);
        }
      } else {
        // default window when no events
        setMinTime(timeOnlyDate(8, 0));
        setMaxTime(timeOnlyDate(18, 0));
      }

    } catch (err) {
      console.error("Error fetching events:", err);
      setAllCalEvents([]);
      setcalEvents([]);
    }
  };

  const handleUserTypeChange = (event) => {
    const value = event.target.value;
    setUserTypeFilter(value);

    const filtered =
      value && value !== ""
        ? allCalEvents.filter((e) => e.UserType === value)
        : allCalEvents;

    setcalEvents(filtered);

    if (filtered.length) {
      const starts = filtered.map((e) => e.start.getTime());
      const ends = filtered.map((e) => e.end.getTime());
      setMinTime(new Date(Math.min(...starts)));
      setMaxTime(new Date(Math.max(...ends)));
    }
  };

  // Assign colors to subjects
  useEffect(() => {
    const colors = {};
    calevents.forEach((calevent) => {
      if (!colors[calevent.SubjectNameCode]) {
        colors[calevent.SubjectNameCode] = getSubjectColor(calevent.SubjectNameCode);
      }
    });
    setSubjectColors(colors);
  }, [calevents]);

  const eventStyleGetter = (calevent) => {
    const backgroundColor = subjectColors[calevent.SubjectNameCode] || COLORS.primaryBlue;
    return {
      style: {
        backgroundColor,
        borderRadius: "6px",
        opacity: 0.9,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: COLORS.pageBg, // ✅ page background
        padding: 3,
        borderRadius: 2,
        border: `1px solid ${COLORS.border}`,
      }}
    >
      <Box sx={{ padding: 0 }}>
        {/* ✅ Header + Admin Logo Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box sx={{ width: 208, height: 108, display: { xs: "none", sm: "block" } }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              width: "100%",
              order: { xs: 1, sm: 0 },
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                color: COLORS.text,
                width: "100%",
                px: { xs: 1, sm: 0 },
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
            >
              {userCampus?.CampusName || ""}
            </Typography>
          </Box>

          {/* LOGO WITH EDIT ACTIONS */}
          <Box
            ref={logoWrapRef}
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-end" },
              width: { xs: "100%", sm: "auto" },
              order: { xs: 0, sm: 0 },
            }}
          >
            <Box
              onClick={toggleLogoActions}
              sx={{
                position: "relative",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isAdmin ? "pointer" : "default",
                userSelect: "none",
                transition: "all 0.15s ease",
                width: { xs: "100%", sm: 208 },
                maxWidth: { xs: 260, sm: 208 },
                height: { xs: 88, sm: 108 },
                ...(isAdmin
                  ? {
                    "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
                  }
                  : {}),
              }}
              title={isAdmin ? "Click to manage logo" : ""}
            >
              {logoPreview ? (
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Campus Logo"
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 2,
                    objectFit: "contain",
                    backgroundColor: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    p: 0.5,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: 2,
                    backgroundColor: COLORS.surface,
                    border: `1px dashed ${COLORS.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: COLORS.textMuted,
                    fontSize: 12,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  {isAdmin ? "Click to add logo" : "No logo"}
                </Box>
              )}

              {isAdmin && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleLogoActions();
                  }}
                  sx={{
                    position: "absolute",
                    right: 8,
                    bottom: 8,
                    width: 32,
                    height: 32,
                    backgroundColor: COLORS.accentOrange,
                    color: "#fff",
                    border: "2px solid #fff",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                    "&:hover": {
                      backgroundColor: "#d96517",
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            {showLogoActions && isAdmin && (
              <Paper
                elevation={8}
                sx={{
                  position: "absolute",
                  top: { xs: 96, sm: 116 },
                  right: 0,
                  zIndex: 20,
                  minWidth: 260,
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.surface,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Stack spacing={1}>
                  <Typography variant="subtitle2" sx={{ color: COLORS.text, fontWeight: 700 }}>
                    Campus Logo
                  </Typography>

                  <Button
                    variant="outlined"
                    onClick={pickLogo}
                    sx={{
                      borderColor: COLORS.primaryBlue,
                      color: COLORS.primaryBlue,
                      "&:hover": { borderColor: COLORS.primaryBlue },
                    }}
                  >
                    {logoPreview ? "Change Logo" : "Choose Logo"}
                  </Button>

                  {logoFile && (
                    <Typography variant="caption" sx={{ color: COLORS.textMuted }}>
                      Selected: {logoFile.name}
                    </Typography>
                  )}

                  {logoError && <Alert severity="error">{logoError}</Alert>}

                  {uploadingLogo && <LinearProgress />}

                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={removeLogoSelection}
                      disabled={uploadingLogo}
                    >
                      Cancel
                    </Button>

                    <Button
                      size="small"
                      variant="contained"
                      onClick={uploadCampusLogo}
                      disabled={!logoFile || uploadingLogo}
                      sx={{
                        backgroundColor: COLORS.accentOrange,
                        "&:hover": { backgroundColor: "#d96517" },
                      }}
                    >
                      Save
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            )}

            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={onLogoSelect}
            />
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ color: COLORS.text }}>
          Welcome, {userFullName?.FullName || ""}
        </Typography>

        {error && (
          <Snackbar open autoHideDuration={3000} onClose={() => setError("")}>
            <Alert severity={error.includes("Success") ? "success" : "info"} onClose={() => setError("")}>{error}</Alert>
          </Snackbar>
        )}

        <Grid container spacing={3}>
          {/* Fee Due */}
          {isStudent ? (
            <Grid item xs={12} sm={4}>
              <Card sx={cardSx(TINTS.dangerBg, TINTS.danger)}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: COLORS.textMuted }}>
                    Fee Due
                  </Typography>
                  <Typography variant="h6" sx={{ color: TINTS.danger, fontWeight: 700 }}>
                    ₹{Number(feeSummary.TotalDue || 0).toLocaleString("en-IN")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  ...cardSx(TINTS.dangerBg, TINTS.danger),
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => setFeeDueExpanded((p) => !p)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ color: COLORS.textMuted }}>
                      Fee Due
                    </Typography>
                    {feeDueExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>

                  <Typography variant="h6" sx={{ color: TINTS.danger, fontWeight: 700 }}>
                    StudentCount: {allstudentsfeeSummary.StudentCount || 0}
                  </Typography>
                  <Typography variant="h6" sx={{ color: TINTS.danger, fontWeight: 700 }}>
                    TotalDue: ₹
                    {Number(allstudentsfeeSummary.TotalDue || 0).toLocaleString("en-IN")}
                  </Typography>

                  <Collapse in={feeDueExpanded} timeout="auto" unmountOnExit>
                    {renderFeeDetailsTable()}
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Attendance */}
          <Grid item xs={12} sm={4}>
            <Card sx={cardSx(TINTS.infoBg, COLORS.primaryBlue)}>
              <CardContent>
                <Typography variant="h6" sx={{ color: COLORS.textMuted }}>
                  Attendance
                </Typography>
                <Typography variant="h6" sx={{ color: TINTS.info, fontWeight: 700 }}>
                  {attendance.AttendedClasses || 0}/{attendance.TotalClasses || 0} Days
                </Typography>
                <Typography variant="h6" sx={{ color: TINTS.info, fontWeight: 700 }}>
                  Percentage: {attendance.AttendancePercentage || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Announcements */}
          <Grid item xs={12} sm={4}>
            <Card sx={cardSx(TINTS.successBg, TINTS.success)}>
              <CardContent>
                <Typography variant="h6" sx={{ color: COLORS.textMuted }}>
                  Announcements
                </Typography>

                {announcements.length > 0 ? (
                  announcements.slice(0, 3).map((announcement, index) => (
                    <Accordion
                      key={index}
                      disableGutters
                      elevation={0}
                      square
                      sx={{
                        // ✅ remove outer box / border / shadow
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        outline: "none",
                        "&::before": { display: "none" }, // default divider line
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          // ✅ remove the “row box” look
                          backgroundColor: "transparent",
                          boxShadow: "none",
                          border: "none",
                          outline: "none",

                          // ✅ remove hover/focus/expanded backgrounds & borders
                          "&:hover": { backgroundColor: "transparent" },
                          "&.Mui-expanded": { backgroundColor: "transparent" },
                          "&.Mui-focusVisible": { backgroundColor: "transparent" },

                          // ✅ remove any hairline borders some themes apply
                          "& .MuiAccordionSummary-content": { margin: 0 },
                          "&.Mui-expanded .MuiAccordionSummary-content": { margin: 0 },

                          // optional spacing
                          px: 0,
                          py: 0.5,
                          minHeight: 32,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: TINTS.success, lineHeight: "1.1", mt: 0.5 }}
                        >
                          {announcement.Title}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          // ✅ remove inner description box look
                          backgroundColor: "transparent",
                          border: "none",
                          boxShadow: "none",
                          outline: "none",
                          px: 0,
                          pt: 0,
                          pb: 0.75,
                        }}
                      >
                        <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                          {announcement.Message}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                    No recent announcements.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Schedule + Assignments */}
        <List>
          <Paper elevation={0} sx={{ mt: 3, p: 2, ...surfacePaperSx }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ color: COLORS.textMuted }}>
                Today's Schedule
                <IconButton onClick={handleCollapseToggle}>
                  {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              </Typography>

              <FormControl
                size="small"
                sx={{
                  minWidth: 140,
                  "& .MuiInputBase-root": { height: 34 },
                  "& .MuiSelect-select": { padding: "6px 10px" },
                }}
              >
                <InputLabel id="user-type-label">User Type</InputLabel>
                <Select
                  labelId="user-type-label"
                  label="User Type"
                  value={userTypeFilter}
                  onChange={handleUserTypeChange}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Student">Student</MenuItem>
                  <MenuItem value="ClassRepresentative">Class Representative</MenuItem>
                  <MenuItem value="Teacher">Teacher</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Collapse in={!isCollapsed}>
              <Box sx={{ height: "80vh", p: 0 }}>
                <Calendar
                  localizer={localizer}
                  events={calevents}
                  date={currentDate}
                  view={view}
                  onView={handleViewChange}
                  onNavigate={(date) => setCurrentDate(date)}
                  startAccessor="start"
                  endAccessor="end"
                  style={{
                    height: "100%",
                    backgroundColor: COLORS.pageBg,
                    borderRadius: 8,
                    border: `1px solid ${COLORS.border}`,
                  }}
                  //min={new Date(minTime.setMinutes(0, 0, 0))}
                  //max={new Date(maxTime.setMinutes(0, 0, 0))}
                  min={minTime}
                  max={maxTime}
                  resizable
                  eventPropGetter={eventStyleGetter}
                />
              </Box>
            </Collapse>
          </Paper>

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" sx={{ color: COLORS.text }}>
            Upcoming Assignments
          </Typography>

          {assignments.map((task, index) => (
            <ListItem key={index} sx={{ px: 0 }}>
              <ListItemText
                primary={<span style={{ color: COLORS.text }}>{task.title}</span>}
                secondary={<span style={{ color: COLORS.textMuted }}>{`Due: ${task.dueDate}`}</span>}
              />
            </ListItem>
          ))}
        </List>

        {/* Events */}
        <Grid item xs={12} sm={4}>
          <Card sx={cardSx(TINTS.warnBg, COLORS.accentOrange)}>
            <CardContent>
              <Typography variant="h6" sx={{ color: COLORS.textMuted }}>
                Upcoming Events
              </Typography>

              {events.length > 0 ? (
                events.slice(0, 3).map((event, index) => (
                  <Accordion
                    key={index}
                    disableGutters
                    elevation={0}
                    square
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      outline: "none",
                      "&::before": { display: "none" }, // remove default divider
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        border: "none",
                        outline: "none",

                        // remove hover / focus / expanded backgrounds
                        "&:hover": { backgroundColor: "transparent" },
                        "&.Mui-expanded": { backgroundColor: "transparent" },
                        "&.Mui-focusVisible": { backgroundColor: "transparent" },

                        // spacing + no margin jump
                        px: 0,
                        py: 0.5,
                        minHeight: 32,
                        "& .MuiAccordionSummary-content": { margin: 0 },
                        "&.Mui-expanded .MuiAccordionSummary-content": { margin: 0 },

                        // icon color polish
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          color: COLORS.textMuted,
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: TINTS.warn, lineHeight: "1.1" }}
                      >
                        {event.Title}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        backgroundColor: "transparent",
                        border: "none",
                        boxShadow: "none",
                        outline: "none",
                        px: 0,
                        pt: 0,
                        pb: 0.75,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                            📅 {event.EventDate} | 🕒 {event.Time}
                          </Typography>
                          <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                            📍 {event.Location}
                          </Typography>
                          <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                            {event.Description}
                          </Typography>
                        </Box>
                        {event.EventFlyer && (
                          <Box
                            component="img"
                            src={event.EventFlyer.startsWith("http") ? event.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${event.EventFlyer}`}
                            alt="Event Flyer"
                            onClick={() => setSelectedImage(event.EventFlyer.startsWith("http") ? event.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${event.EventFlyer}`)}
                            sx={{
                              width: 90,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: `1px solid ${COLORS.border}`,
                              flexShrink: 0,
                              cursor: "pointer",
                              transition: "transform 0.2s",
                              "&:hover": {
                                transform: "scale(1.05)",
                              }
                            }}
                          />
                        )}
                      </Box>
                      {(() => {
                        const available = event.SeatsCount && !isNaN(event.SeatsCount)
                          ? Math.max(0, event.SeatsCount - (event.RegisteredCount || 0))
                          : null;
                        const seatsFull = available !== null && available <= 0;
                        return (
                          <>
                            <Typography variant="body2" sx={{ color: seatsFull ? TINTS.danger : COLORS.textMuted, mt: 0.5, fontWeight: seatsFull ? 700 : 500 }}>
                              {seatsFull ? '🪑 Seats are full!' : `🪑 Seats: ${available !== null ? `${available}/${event.SeatsCount}` : 'N/A'}`}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={!!event.IsUserRegistered || seatsFull}
                                sx={{
                                  borderColor: (event.IsUserRegistered || seatsFull) ? COLORS.textMuted : COLORS.accentOrange,
                                  color: (event.IsUserRegistered || seatsFull) ? COLORS.textMuted : COLORS.accentOrange,
                                  borderStyle: 'dashed',
                                  '&:hover': { backgroundColor: (event.IsUserRegistered || seatsFull) ? 'transparent' : 'rgba(240, 120, 40, 0.08)' }
                                }}
                                onClick={() => handleOpenRegModal(event)}
                              >
                                Register
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={!event.IsUserRegistered}
                                sx={{
                                  borderColor: !event.IsUserRegistered ? COLORS.textMuted : '#0284c7',
                                  color: !event.IsUserRegistered ? COLORS.textMuted : '#0284c7',
                                  borderStyle: 'dashed',
                                  '&:hover': { backgroundColor: !event.IsUserRegistered ? 'transparent' : 'rgba(2, 132, 199, 0.08)' }
                                }}
                                onClick={() => handleOpenUnregModal(event)}
                              >
                                Unregister
                              </Button>
                            </Box>
                          </>
                        );
                      })()}
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                  No upcoming events.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Box>

      {/* Registration Modal */}
      <Dialog
        open={openRegModal}
        onClose={handleCloseRegModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            padding: 1,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 24px 48px -12px rgba(25, 118, 210, 0.25)",
            border: "1px solid rgba(255, 255, 255, 0.3)"
          }
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(236, 240, 243, 0.7)', backdropFilter: 'blur(4px)' } }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <Typography variant="h4" sx={{
            fontWeight: 800,
            background: `linear-gradient(135deg, ${COLORS.primaryBlue} 0%, ${COLORS.accentOrange} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px'
          }}>
            Register Now
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5, fontWeight: 500 }}>
            {selectedEventForReg?.Title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, pt: '12px !important' }}>

        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={handleRegSubmit}
            variant="contained"
            disableElevation
            fullWidth
            sx={{
              background: `linear-gradient(135deg, ${COLORS.primaryBlue} 0%, ${COLORS.accentOrange} 100%)`,
              color: '#fff',
              py: 1.2,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 8px 16px rgba(240, 120, 40, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 20px rgba(240, 120, 40, 0.4)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Confirm Registration
          </Button>
          <Button
            onClick={handleCloseRegModal}
            fullWidth
            sx={{ color: COLORS.textMuted, textTransform: 'none', fontWeight: 600, borderRadius: 3, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unregistration Modal */}
      <Dialog
        open={openUnregModal}
        onClose={handleCloseUnregModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            padding: 1,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 24px 48px -12px rgba(107, 114, 128, 0.25)",
            border: "1px solid rgba(255, 255, 255, 0.3)"
          }
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: 'rgba(236, 240, 243, 0.7)', backdropFilter: 'blur(4px)' } }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <Typography variant="h4" sx={{
            fontWeight: 800,
            background: `linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px'
          }}>
            Unregister
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5, fontWeight: 500 }}>
            {selectedEventForUnreg?.Title}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, pt: '12px !important' }}>
          <Typography variant="body1" textAlign="center" color="textSecondary">
            Are you sure you want to unregister from this event?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={handleUnregSubmit}
            variant="contained"
            disableElevation
            fullWidth
            sx={{
              background: `linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)`,
              color: '#fff',
              py: 1.2,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 3,
              textTransform: 'none',
              boxShadow: '0 8px 16px rgba(2, 132, 199, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 20px rgba(2, 132, 199, 0.4)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            Confirm Unregistration
          </Button>
          <Button
            onClick={handleCloseUnregModal}
            fullWidth
            sx={{ color: COLORS.textMuted, textTransform: 'none', fontWeight: 600, borderRadius: 3, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Lightbox Dialog */}
      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(8px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Box
          component="img"
          src={selectedImage}
          alt="Enlarged Event Flyer"
          sx={{
            maxWidth: "100%",
            maxHeight: "75vh",
            objectFit: "contain",
            borderRadius: 3,
            cursor: "pointer",
            border: "4px solid #fff",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
          }}
          onClick={() => setSelectedImage(null)}
        />
      </Dialog>
    </Paper>
  );
};

export default HomePage;